import { TZDate } from '@date-fns/tz'
import { ClassValue, clsx } from 'clsx'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import { parseISO } from 'date-fns/parseISO'
import DOMPurify from 'dompurify'
import { FormikHelpers } from 'formik'
import uniq from 'lodash/uniq'
import queryString from 'query-string'
import React from 'react'
import { toast } from 'react-toastify'

import { DATE_FORMATS, ERROR_MESSAGES } from '../../constants/constants'
import { ConstraintViolationListError, StringMap } from '../../types/misc'
import { constraintViolationService } from '../services/constraintViolationService'
import { errorTypeService } from '../services/errorTypeService'
import { scrollToFirstError } from './domHelpers'

// TODO: Use TypeScript to infer the type of queryParams based on the URL.

type QueryParams = Record<string | 'successFlash', string>

export function url(url: string, queryParams: QueryParams) {
  return queryString.stringifyUrl({ url, query: queryParams })
}

/**
 * @see https://date-fns.org/docs/format for date formats.
 */
export function formatDate(
  date: Date | string,
  dateFormat = DATE_FORMATS.DAY_MONTH_YEAR,
  options?: { timeZone?: string | null },
): string {
  let dateObj = date instanceof Date ? date : parseISO(date)
  const { timeZone } = options || {}

  if (timeZone) {
    dateObj = new TZDate(dateObj.toISOString(), timeZone)
  }

  return format(dateObj, dateFormat)
}

export function convertDate(
  dateString: string,
  fromFormat: string,
  toFormat: string,
): string {
  const parsedDate = parseDate(dateString, fromFormat)
  return formatDate(parsedDate, toFormat)
}

export function parseDate(dateString: string, dateFormat: string) {
  return parse(dateString, dateFormat, new Date())
}

export function somethingWentWrong() {
  toast.error(ERROR_MESSAGES.genericServerError)
}

// Get the values of a FormData object as a plain object.
// This handles fields that are:
// - A single string value
// - An array of string values
// - A single file value
export async function getFormValuesFromRequest(
  request: Request,
): Promise<Record<string, FormDataEntryValue | FormDataEntryValue[]>> {
  const formValues: Record<string, FormDataEntryValue | FormDataEntryValue[]> =
    {}
  const formData = await request.formData()
  const fieldNames = uniq(Array.from(formData.keys()))

  fieldNames.forEach((fieldName) => {
    const fieldValue = formData.getAll(fieldName)

    if (fieldValue.length > 1) {
      // If the field value obtained from calling formData.getAll(fieldName)
      // returns an array with a length > 1, then the initial field was an array
      // field so add the value as is (an array)
      formValues[fieldName] = fieldValue
    } else {
      // Otherwise, the initial field must have been a single string or file
      // field so don't add the value as an array
      formValues[fieldName] = fieldValue[0] || ''
    }
  })

  return formValues
}

export function handleApiError({
  error,
  formikHelpers,
}: {
  error: unknown
  formikHelpers: FormikHelpers<any> // eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  if (errorTypeService.isConstraintViolationListError(error)) {
    handleConstraintViolationListError({ error, formikHelpers })
    return
  }

  somethingWentWrong()
}

export function handleConstraintViolationListError({
  error,
  formikHelpers,
}: {
  error: ConstraintViolationListError
  formikHelpers: FormikHelpers<any> // eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const formattedErrors = constraintViolationService.formatErrors(error)
  formikHelpers.setErrors(formattedErrors)
  scrollToFirstError()
}

// TODO: Use TypeScript to infer the type of queryParams based on the URL.
export function buildUrl(path: string, context?: BuildUrlContext): string {
  const params = context?.params || {}

  for (const paramName in params) {
    const paramValue = params[paramName]

    if (!paramValue) {
      throw new Error(
        `Expected paramValue to be set for paramName "${paramName}"`,
      )
    }

    path = path.replace(`:${paramName}`, paramValue.toString())
  }

  return queryString.stringifyUrl({ url: path, query: context?.searchParams })
}

interface BuildUrlContext {
  params?: StringMap
  searchParams?: StringMap & {
    backLabel?: string
    backUrl?: string
  }
}

// Get text content of React element
// Inspired by https://stackoverflow.com/a/60564620/2302835
export function getNodeText(
  node: React.ReactElement | string | number,
): string {
  if (!node) {
    return ''
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return node.toString()
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join('')
  }

  return getNodeText(node.props.children)
}

export function getTooltipClassName(text: React.ReactElement | string): string {
  const length = getNodeText(text).length

  switch (true) {
    case length > 900:
      return 'tooltip-xl'
    case length > 200:
      return 'tooltip-lg'
    case length > 20:
      return 'tooltip-md'
    default:
      return ''
  }
}

export function isNewReactSelectOption(option: unknown): option is {
  label: string
  value: string
} {
  return (
    typeof option === 'object' &&
    option !== null &&
    'label' in option &&
    'value' in option &&
    '__isNew__' in option &&
    option['__isNew__'] === true
  )
}

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs)
}

export function slugify(str: string) {
  return str
    .toLowerCase()
    .replace(/\s/g, '-')
    .replace(/[^\w-]/g, '')
}

export function formatMoney(
  moneyValue: number,
  numberFormatOptions?: Intl.NumberFormatOptions,
): string {
  const formattedAmount = Intl.NumberFormat('en-GB', {
    notation: 'standard',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...numberFormatOptions,
  }).format(moneyValue)

  return `£${formattedAmount}`
}

export function isMacOS() {
  return navigator.platform.toLowerCase().includes('mac')
}

export function isDeleteShortcut(event: KeyboardEvent) {
  return isMacOS()
    ? event.metaKey && event.key === 'Backspace'
    : event.ctrlKey && event.key === 'Backspace'
}

/**
 * Use to sanitize user input where safe HTML tags should be allowed.
 *
 * The list of allowed tags and attributes can be found by inspecting the
 * exported `html` object in the following places:
 * - https://github.com/cure53/DOMPurify/blob/main/src/tags.ts
 * - https://github.com/cure53/DOMPurify/blob/main/src/attrs.ts
 */
export function sanitizeHtml(str: string) {
  return DOMPurify.sanitize(str)
}

/**
 * Use to sanitize user input where no HTML should be allowed.
 */
export function sanitizeText(str: string) {
  return DOMPurify.sanitize(str, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  })
}
